import { createMemo, createSignal, createEffect, onMount, onCleanup, Accessor, Signal, createContext, useContext } from 'solid-js'
import { For, Show } from 'solid-js'
import { GridColumnDef, GridData, ViewGridDef } from 'st-app/api'
import { ExtendedRowData } from './ExtendedRowData'
import { STGridEvents } from './STGridEvents'
import { grid as api } from 'st-app/api'
import { requireFindResultPanelContext } from '../st-find-result-panel/FindResultPanelContext'
import styles from './st-grid.module.css'
import './st-grid.css'

const ST = (window as any).ST || {};

export class GridContextType {
    definition: Signal<ViewGridDef | undefined>;
    data: Signal<GridData | undefined>;
    headerRows: Accessor<any[]>;

    constructor(definition?: ViewGridDef | undefined) {
        this.data = createSignal<GridData>();
        this.definition = createSignal(definition);

        this.headerRows = createMemo(() => {
            const def = this.definition[0]();

            return def?.columns
                .reduce((arr: any, col: any) => {
                    let rowIdx = insertSupertitles(col, 0);
                    arr[rowIdx].push(col);

                    function insertSupertitles(c: any, rowIdx: number): number {
                        if (!arr[rowIdx]) {
                            arr.push([])
                        }

                        c.supertitles.forEach((id: string) => {
                            const st = def!.supertitles[id];
                            const row = arr[rowIdx];

                            if (row[row.length - 1] !== st) {
                                row.push(st);
                            }
                            st.colSpan++;

                            rowIdx++;
                            if (!arr[rowIdx]) {
                                arr.push([])
                            }
                        });

                        return rowIdx;
                    }

                    return arr;
                }, []) || []
        });
    }
}

export const GridContext = createContext<GridContextType>({} as GridContextType);

export class STGridProps {
    viewId: string = '';
    gridId: string = '';
    groupGu?: string = undefined;

    events: STGridEvents = new STGridEvents();
    selectedRows: Signal<string[]> = createSignal<string[]>([]);

    ctx?: GridContextType;
}


//const CELL_HEIGHT = 32;
const EMPTY_ROW: ExtendedRowData = {
    ...new ExtendedRowData(),

    identity: 'EMPTY_ROW',
    values: {} as Array<string>
};

export default function STGrid(props: STGridProps) {
    const context = props.ctx ?? new GridContextType();
    const frpContext = requireFindResultPanelContext();

    const [definition, setDefinition] = context.definition;

    let gridDefTimer: NodeJS.Timeout;
    createEffect(() => {
        const { viewId, gridId } = props;
        const viewState = frpContext.viewState();

        if (viewId && gridId && viewState.serverState?.PrimaryObjectGU) {
            clearTimeout(gridDefTimer);
            gridDefTimer = setTimeout(() => {
                api.GetGridDefinition(viewId, gridId, viewState)
                    .then(setDefinition);
            }, 100);
        }
    });

    return <GridContext.Provider value={context}>
        <Show when={definition()} fallback={<Loading />}>
            {STGridInternal(props)}
        </Show>
    </GridContext.Provider>
}

STGrid.defaultProps = new STGridProps();

function STGridInternal(props: STGridProps) {
    const frpContext = requireFindResultPanelContext();
    const context : GridContextType = useContext(GridContext);
    const { viewId, gridId, groupGu } = props;

    const [gridData, setGridData] = context.data;

    const [startRecord, setStartRecord] = createSignal<number>(0);
    const [rowsToRender, setRowsToRender] = createSignal<Array<ExtendedRowData>>();
    const [cellHeight, setCellHeight] = createSignal<number>(24);
    const [maxRows, setMaxRows] = createSignal<number>(20);
    const [forceRefresh, setForceRefresh] = createSignal<boolean>(false);
    const [selectedRows, setSelectedRows] = props.selectedRows;

    let container: HTMLDivElement | undefined;
    let tableContainer: HTMLDivElement | undefined;
    let thead: HTMLTableSectionElement | undefined;

    const onContainerResized = (event?: Event) => {
        if (container && thead) {
            setMaxRows(Math.floor((container.clientHeight - thead.clientHeight) / cellHeight()));
        }
    };

    // monitor the container's height for changes and update the constraints
    {
        let heightInterval: any;
        onMount(() => {
            let origHeight = 0;
            heightInterval = setInterval(() => {
                if (container && origHeight != container.clientHeight) {
                    origHeight == container.clientHeight;
                    onContainerResized();
                }
            }, 200);
        });

        onCleanup(() => {
            clearInterval(heightInterval);
        });
    }

    // recompute container height/max rows when the cell height changes
    createEffect(() => {
        const ch = cellHeight();
        if (container) {
            onContainerResized();
        }
    });

    // recompute the min width whenever our rows are updated
    createEffect(() => {
        const sr = startRecord();
        if (sr > 0 && tableContainer) {
            const minWidth = Math.floor(Math.max(tableContainer.clientWidth, parseInt(tableContainer.style.minWidth)));
            tableContainer.style.minWidth = `${minWidth}px`
        }
    });

    createEffect(() => {
        const rows = rowsToRender()
            ?.filter(row => row.isHighlighted())
            .map(row => row.identity || '');

        setSelectedRows(rows || []);
    })

    let isUpdatingSelection = false;
    createEffect(() => {
        const selected = selectedRows();
        const rows = rowsToRender() || [];

        if (!isUpdatingSelection) {
            isUpdatingSelection = true;
            for (const row of rows) {
                if (row.identity && selected.includes(row.identity)) {
                    row.setIsHighlighted(true);
                }
                else {
                    row.setIsHighlighted(false);
                }
            }
            isUpdatingSelection = false;
        }
    });

    // reset the min width whenever data is reset
    createEffect(() => {
        const sc = frpContext.searchCriteria();

        if (tableContainer) {
            tableContainer.style.minWidth = '';
        }
    });

    function Grid() {
        onMount(() => {
            window.addEventListener('resize', onContainerResized);
            onContainerResized();
        });

        onCleanup(() => {
            window.removeEventListener('resize', onContainerResized);
        })

        const [definition, setDefinition] = context.definition;

        const scrollHeight = createMemo(() => (gridData()?.totalRows || 0) * cellHeight())
        return (<div class="st-grid flexbox vertical">
            <Show when={definition()?.title}>
                <h2>{definition()!.title}</h2>
            </Show>
            <Show when={!!gridData()}>
                <div style="text-align: right; padding: 5px 10px">
                    Total Rows: <strong>{gridData()!.totalRows.toLocaleString()}</strong> ({gridData()!.duration}ms)
                </div>
            </Show>
            <div ref={container} class="st-grid-container auto" onScroll={onScroll}>
                <div class="virtual-content" style={{
                    height: scrollHeight() + 'px'
                }}></div>
                <div ref={tableContainer} style="position: sticky; top: 0; max-height: 12px; padding-right: 10px">
                    <table style="width: 100%">
                        <thead ref={thead}>
                            <For each={context.headerRows()}>{(columns, idx) => (
                                <tr>
                                    <Show when={definition()!.showLineSelector && idx() === 0}>
                                        <th rowspan={context.headerRows().length}>Line</th>
                                    </Show>
                                    <For each={columns}>{(col, idx) => (
                                        <th scope="col" rowspan={col?.rowSpan || 1} colspan={col?.colSpan || 1}>
                                            {col.label}
                                        </th>
                                    )}</For>
                                </tr>
                            )}</For>
                        </thead>
                        <tbody>
                            <For each={rowsToRender()}>{(row, idx) => GridRow(row, idx())}</For>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>);
    }

    function GridRow(row: ExtendedRowData, rowIdx: number) {
        const context : GridContextType = useContext(GridContext);

        const [definition, setDefinition] = context.definition;

        function initRowElement(el: HTMLTableRowElement) {
            window.requestAnimationFrame(() => {
                setCellHeight(Math.max(cellHeight(), el.clientHeight));
            });
        }

        const skelProps = new SkeletonProps();
        skelProps.shape = Shapes.Rectangle;

        return <tr ref={initRowElement} onClick={onRowClicked.bind(row)} classList={{ selected: row.isHighlighted() }}>
            <Show when={definition()!.showLineSelector}>
                <th>{startRecord() + rowIdx + 1}</th>
            </Show>

            <Show when={row === EMPTY_ROW}>
                <For each={Array(definition()!.columns.length)}>{(col, idx) => (
                    <td class="empty-cell">
                        <Skeleton {...skelProps}></Skeleton>
                    </td>
                )}</For>
            </Show>

            <Show when={row !== EMPTY_ROW}>
                <For each={row.values}>{(col, idx) => (
                    <td>{formatValue(definition()!.columns[idx()], col)}</td>
                )}</For>
            </Show>
        </tr>
    }

    let prevClientWidth = 0;
    function onScroll(this: HTMLDivElement, ev: Event) {
        if (thead) {
            const totalRows = gridData()?.totalRows || 0;
            const pos = Math.ceil((this.scrollTop / (this.scrollHeight - thead.clientHeight - 20)) * totalRows);

            if (this.clientWidth > prevClientWidth) {
                prevClientWidth = this.clientWidth;
                this.style.minWidth = `${this.clientWidth}px`;
            }

            setStartRecord(Math.max(0, Math.min(pos, totalRows - 20)));
        }
    }

    function onRowClicked(this: ExtendedRowData, ev: MouseEvent) {
        const isHighlighted = this.isHighlighted();

        for (var row of allRows) {
            row?.setIsHighlighted(false);
        }

        this.setIsHighlighted(!isHighlighted);

        props.events.onRowClicked?.call(this, this, ev)
    }

    let activeRequest: Promise<GridData>;
    let currentStart = 0;
    let requestPageTimer: NodeJS.Timeout;
    let requestPageRetries = 0;

    function requestPage(start: number, forceRefresh: boolean = false) {
        clearTimeout(requestPageTimer);
        if (start < 0 || (!forceRefresh && start === gridData()?.startRow)) return;

        currentStart = start;
        requestPageTimer = setTimeout(async () => {
            if (activeRequest) await activeRequest;
            if (currentStart !== start) return; // request is stale

            activeRequest = api.LoadData(viewId, gridId, groupGu || '', start, frpContext.viewState());
            const data = await activeRequest;

            if (data) {
                requestPageRetries = 0;
                setGridData(data);
            }
            else if (requestPageRetries++ < 5) {
                requestPage(start);
            }

            if (ST.DEBUGGING_COMPONENT) {
                console.log(data);
            }
        }, 150);
    }

    // When grid data changes, update both allRows and the current rows to render
    let allRows: Array<ExtendedRowData> = [];
    createEffect(() => {
        const data = gridData();
        const start = startRecord();
        const refresh = forceRefresh();
        const totalRowsToRender = Math.min(data?.totalRows || 0, maxRows());

        if (data?.rows) {
            // Fill the window of allRows with the data we retrieved
            let idx = data.startRow;
            data.rows.forEach((r: any) => {
                allRows[idx++] = {
                    ...new ExtendedRowData(),
                    ...r
                };
            });

            // Update the rows to render with the current data
            const res: Array<ExtendedRowData> = [];
            const missingRows: Array<number> = [];
            for (let i = 0; i < totalRowsToRender; i++) {
                const rowIdx = i + start;
                const row = allRows[rowIdx];

                if (rowIdx >= data.totalRows)
                    break;

                if (!row) {
                    missingRows.push(rowIdx);
                    res.push(EMPTY_ROW)
                }
                else {
                    res.push(row);
                }
            }

            // If we missed any records, trigger a data fetch for the missing set of rows
            if (missingRows.length) {
                requestPage(missingRows[0]);
            }

            setRowsToRender(res);
        }
        else {
            requestPage(0);
        }
    });

    createEffect(() => {
        const viewState = frpContext.viewState(); // force binding
        allRows = [];
        setStartRecord(0);
        requestPage(0, true);
    });

    return <Grid />
}

function formatValue(col: GridColumnDef, value: string): any {
    switch (col?.controlType) {
        case 'REV_CHECK_BOX':
            return (<input type="checkbox" aria-label={col.label} checked={value === 'Y'} />);
        case 'REV_DROP_DOWN':
            return (<select><option>{value}</option></select>);
        default:
            return (<span>{value}</span>);
    }
}

enum Shapes {
    Rectangle,
    Circle
}

class SkeletonProps {
    shape: Shapes = Shapes.Rectangle;
    width: string = '100%';
    height: string = '1em';
}

function Skeleton(props: SkeletonProps) {
    return <div class={styles.skeleton} style={{ width: props.width, height: props.height, 'line-height': props.height }}></div>
}

function Loading() {
    return (<div>
        Loading...
    </div>)
}
